import { Promisable } from '@naturalcycles/js-lib'
import { Button as UIKitButton, ButtonLink, ButtonSize, ButtonTarget } from '@naturalcycles/ncuikit'
import { isNotNaturalCyclesURL } from '@src/helpers/nc-navigate'
import { themeToUiKitButton } from '@src/helpers/uikit'
import { mixpanelService } from '@src/srv/mixpanel.service'
import type { MouseEvent, ReactNode } from 'react'

export enum ButtonElements {
  Anchor = 'Anchor',
  Button = 'Button',
}

export interface DatoCmsButton {
  __typename: string
  id: string
  text: string
  url: string
  theme: 'brandPurple' | 'ghost' | 'transparentPurple' | 'shimmerGradient'
}

export interface DatoCmsSignupButton extends Omit<DatoCmsButton, 'url'> {
  utmSource?: string
}

export type ButtonTheme =
  | 'digitalBlue'
  | 'ghost'
  | 'inactiveGhost'
  | 'ghostWhite'
  | 'brandPurple'
  | 'inactiveBrandPurple'
  | 'brandBlue2023'
  | 'brandSalmon'
  | 'transparentPurple'
  | 'white'
  | 'inactiveDigitalBlue'
  | 'whiteBrandPurple'
  | 'inactiveWhiteBrandPurple'
  | 'digitalBlueOutline'
  | 'appearAsLink'
  | 'shimmerGradient'

export interface ButtonProps {
  id?: string
  uid?: string
  children?: any
  theme?: ButtonTheme
  underline?: boolean
  href?: string
  utmTrackingId?: string
  onClick?: (event: MouseEvent) => Promisable<void>
  ariaLabel?: string
  disabled?: boolean
  size?: ButtonSize
  isWide?: boolean
}

async function defaultOnClick(): Promise<void> {}

export function Button({
  id,
  uid,
  children = <></>,
  utmTrackingId = '',
  theme = 'brandPurple',
  href = '',
  onClick = defaultOnClick,
  size,
  ariaLabel,
  disabled = false,
  isWide,
}: ButtonProps): ReactNode {
  const buttonElement = href ? ButtonElements.Anchor : ButtonElements.Button

  async function handleClick(event: MouseEvent): Promise<void> {
    if (disabled) {
      return
    }

    await onClick(event)

    if (!utmTrackingId) {
      // nothing to track
      return
    }

    if (href) {
      // tracked on destination
      return
    }

    mixpanelService.trackClick(utmTrackingId)
  }

  const props = {
    onClick: handleClick,
    'aria-label': ariaLabel,
    uid,
    id,
    size,
    ...themeToUiKitButton(theme),
    isWide,
  }

  const isInternalLink = !isNotNaturalCyclesURL(href)

  return (
    <>
      {buttonElement === ButtonElements.Anchor && (
        <ButtonLink
          target={isInternalLink ? ButtonTarget._SELF : ButtonTarget._BLANK}
          href={href}
          {...props}
        >
          {children}
        </ButtonLink>
      )}

      {buttonElement === ButtonElements.Button && <UIKitButton {...props}>{children}</UIKitButton>}
    </>
  )
}
